/* eslint-disable camelcase -- query params use snake_case (gateway / API) */
import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  SubmissionLoader,
  InfoNotice,
  AppButton,
  RadioButtonSlider,
  SimpleModal,
  FlexibleSelectDropdown,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import {
  initiateFetchClaimsRequest,
  initiateFetchAllClaimsRequest,
  clearContentIdClaimsRequest,
  initiateFetchManualClaimRequests,
  clearManualClaimRequests,
  initiateCreateClaimReleaseRequest,
  initiateRevertClaimReleaseRequest,
  initiateUpdateClaimsSettingsRequest,
  initiateMonetizationRequest,
} from '@Actions';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import {
  ClaimsTablePartial,
  ManualClaimsRequestsTablePartial,
} from '@Components/Partial/Product/ContentId';
import { localAsset } from '@Helpers/asset';
import { StyledSVG } from '@Components/Partial/Product/StyledSharedComponents.jsx';

const STORAGE_KEY = 'youtubeClaimsPageState_';


const ClaimsListPage = ({
  small,
  loading,
  submitLoading,
  location,
  fetchClaimsData,
  fetchAllClaimData,
  clearClaimsData,
  fetchManualClaimRequests,
  clearManualClaimsData,
  submitClaimReleaseRequest,
  submitRevertReleaseRequest,
  updateClaimsSettings,
  submitMonetizationRequest,
  claimsData = null,
  manualClaimRequestsData = null,
  paymentPending = false,
  featureConfig = null,
}) => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [claimsView, setClaimsView] = React.useState(
    locationState?.view === 'manual' ? 'manual' : 'automated',
  );
  const [query, setQuery] = React.useState({});
  const [currentPage, setCurrentPage] = React.useState(0);
  const [manualClaimPage, setManualClaimPage] = React.useState(0);
  const [manualQuery, setManualQuery] = React.useState({});
  const [totalResults, setTotalResults] = React.useState(0);
  const [showSettingsModal, setShowSettingsModal] = React.useState(false);
  const [settings, setSettings] = React.useState({
    enableAutoProcessing: featureConfig?.autoClaimProcessing ?? false,
    shortVideos: featureConfig?.autoProcessingConfig?.shortVideos ?? null,
    longFormVideos: featureConfig?.autoProcessingConfig?.longFormVideos ?? null,
    privateVideos: featureConfig?.autoProcessingConfig?.privateVideos ?? null,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  const memoizedQueryString = React.useMemo(
    () => JSON.stringify(query),
    [query],
  );

  const memoizedManualQueryString = React.useMemo(
    () => JSON.stringify(manualQuery),
    [manualQuery],
  );

  const { assetConfig, autoClaimProcessing, autoProcessingConfig } = featureConfig || {};

  React.useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY + memoizedQueryString);
    if (stored) {
      const { total, page: usePage } = JSON.parse(stored);
      setTotalResults(total);
      setCurrentPage(Math.max(0, usePage));
    }
  }, [memoizedQueryString]);

  React.useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY + memoizedQueryString,
      JSON.stringify({
        total: totalResults,
        page: currentPage,
      }),
    );
  }, [memoizedQueryString, totalResults, currentPage]);

  React.useEffect(() => {
    if (claimsView !== 'automated' || paymentPending) {
      return undefined;
    }
    if (assetId) {
      fetchClaimsData(assetId, {
        ...query,
        skip: currentPage * 25,
        limit: 25,
      });
    } else {
      fetchAllClaimData({
        ...query,
        skip: currentPage * 25,
        limit: 25,
      });
    }

    return () => {
      clearClaimsData();
      clearManualClaimsData();
      sessionStorage.removeItem(STORAGE_KEY + memoizedQueryString);
    };
  }, [memoizedQueryString, totalResults, currentPage, assetId, paymentPending, claimsView]);

  React.useEffect(() => {
    if (claimsView !== 'manual' || paymentPending) {
      return undefined;
    }
    const params = {
      limit: 25,
      offset: manualClaimPage * 25,
      sort_order: manualQuery.sortOrder || 'desc',
    };
    if (manualQuery.status) params.status = manualQuery.status;
    if (manualQuery.createdAfter) params.created_after = manualQuery.createdAfter;
    if (manualQuery.createdBefore) params.created_before = manualQuery.createdBefore;
    if (manualQuery.search) params.search = manualQuery.search;
    fetchManualClaimRequests(params);
    return undefined;
  }, [
    claimsView,
    manualClaimPage,
    memoizedManualQueryString,
    paymentPending,
    fetchManualClaimRequests,
  ]);

  React.useEffect(() => {
    // Reset settings when modal opens
    if (showSettingsModal) {
      setSettings({
        enableAutoProcessing: featureConfig?.autoClaimProcessing ?? false,
        shortVideos: featureConfig?.autoProcessingConfig?.shortVideos ?? null,
        longFormVideos: featureConfig?.autoProcessingConfig?.longFormVideos ?? null,
        privateVideos: featureConfig?.autoProcessingConfig?.privateVideos ?? null,
      });
      setSubmitting(false);
    }
  }, [showSettingsModal, featureConfig]);

  // Pagination handler
  const handlePage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleManualPage = (pageNum) => {
    setManualClaimPage(pageNum);
  };

  const handleManualQueryChange = (key, value) => {
    if (key === 'reset') {
      setManualQuery({});
      setManualClaimPage(0);
    } else {
      setManualQuery((prev) => {
        const next = { ...prev, [key]: value };
        if (!value) delete next[key];
        return next;
      });
      setManualClaimPage(0);
    }
  };

  const handleSubmitClaimRelease = (body, onSuccess) => {
    submitClaimReleaseRequest(body).then((res) => {
      if (res && onSuccess) onSuccess();
    });
  };

  const handleSubmitReleaseRevert = (body, onRefresh) => {
    submitRevertReleaseRequest(body).then(() => {
      if (onRefresh) onRefresh();
    });
  };

  const handleCsvExport = () => {
    if (assetId) {
      fetchClaimsData(assetId, {
        ...query,
      }, true);
    } else {
      fetchAllClaimData({
        ...query,
      }, true);
    }
  };

  const refreshData = () => {
    if (assetId) {
      fetchClaimsData(assetId, {
        ...query,
        skip: currentPage * 25,
        limit: 25,
      });
    } else {
      fetchAllClaimData({
        ...query,
        skip: currentPage * 25,
        limit: 25,
      });
    }
  };

  const handleQueryChange = (key, value) => {
    if (key === 'reset') {
      setQuery({});
      setTotalResults(0);
      setCurrentPage(0);
      sessionStorage.removeItem(STORAGE_KEY + JSON.stringify({}));
    } else {
      setQuery((prev) => {
        const next = { ...prev, [key]: value };
        Object.keys(next).forEach((k) => {
          if (
            next[k] === ''
            || next[k] === null
            || typeof next[k] === 'undefined'
          ) {
            delete next[k];
          }
        });
        return next;
      });
      setTotalResults(0);
      setCurrentPage(0);
      sessionStorage.removeItem(STORAGE_KEY + JSON.stringify(query));
    }
  };

  const selectOptions = [
    { label: 'Monetize', value: 'Monetize' },
    { label: 'Takedown', value: 'Takedown' },
  ];

  const configSettings = {
    enableAutoProcessing: autoClaimProcessing ?? false,
    shortVideos: autoProcessingConfig?.shortVideos ?? null,
    longFormVideos: autoProcessingConfig?.longFormVideos ?? null,
    privateVideos: autoProcessingConfig?.privateVideos ?? null,
  };

  const settingsChanged = settings.enableAutoProcessing !== configSettings.enableAutoProcessing
    || settings.shortVideos !== configSettings.shortVideos
    || settings.longFormVideos !== configSettings.longFormVideos
    || settings.privateVideos !== configSettings.privateVideos;

  const canSubmit = settingsChanged
    && !submitting
    && (
      (settings.enableAutoProcessing
        ? settings.shortVideos && settings.longFormVideos && settings.privateVideos
        : true)
    );

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSettingsSubmit = () => {
    setSubmitting(true);
    updateClaimsSettings(settings);
    setSubmitting(false);
    setShowSettingsModal(false);
  };

  const claimDetailsPath = (claimId) => paths.productClaimDetails.replace(':id', claimId);
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const setView = (view) => {
    setClaimsView(view);
    if (view === 'manual') {
      setManualClaimPage(0);
    }
    if (view === 'automated') {
      setCurrentPage(0);
    }
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manage claims',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box
            direction="row"
            justify="start"
            align="center"
            fill="horizontal"
            wrap
            gap="small"
          >
            <RadioButtonSlider
              radioOptions={[
                { label: 'Automated claims', value: 'automated' },
                { label: 'Manual claim requests', value: 'manual' },
              ]}
              componentBg={page.altBg}
              textColor={page.primaryText}
              activeButtonBg={isDarkMode ? page.highlight : 'white'}
              updateParent={setView}
              value={claimsView}
            />
            {claimsView === 'manual' && (
              <AppButton
                overrideHover
                icon={(
                  <Box pad={{ top: '0.2rem' }}>
                    <StyledSVG
                      src={localAsset('images/upload-file-icon.svg')}
                      width={small ? '1.5rem' : '1.25rem'}
                      height={small ? '1.25rem' : '1rem'}
                      title="Upload claims"
                      $fillColor={isDarkMode ? button.primaryText : page.primaryText}
                    />
                  </Box>
                )}
                level="listRequestNew"
                label={(
                  <Text size="0.85rem" weight={600} color={isDarkMode ? button.primaryText : page.primaryText}>
                    Upload claims
                  </Text>
                )}
                color={isDarkMode ? button.primaryText : page.primaryText}
                bgColor={isDarkMode ? button.primaryBg : page.background}
                fontWeight={600}
                onClick={() => navigate(paths.productManualClaimSubmit)}
                disabled={paymentPending}
                width="fit-content"
              />
            )}
            {claimsView === 'automated' && (
              <AppButton
                overrideHover
                icon={(
                  <Box pad={{ top: '0.2rem' }}>
                    <StyledSVG
                      src={localAsset('images/settings-icon.svg')}
                      width={small ? '1.5rem' : '1.25rem'}
                      height={small ? '1.25rem' : '1rem'}
                      title="Configure claims settings"
                      $fillColor={isDarkMode ? button.primaryText : page.primaryText}
                    />
                  </Box>
                )}
                label={(
                  <Text size="0.85rem" weight={600} color={isDarkMode ? button.primaryText : page.primaryText}>
                    Configure claims settings
                  </Text>
                )}
                color={isDarkMode ? button.primaryText : page.primaryText}
                bgColor={isDarkMode ? button.primaryBg : page.background}
                fontWeight={600}
                onClick={() => setShowSettingsModal(true)}
                level="listRequestNew"
                width="fit-content"
              />
            )}
          </Box>
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={page.highlight}
          message="Processing request..."
          contentWidth="20rem"
        />
      )}
      {showSettingsModal && (
        <SimpleModal
          noClickOutside
          hideLowerSection
          small={small}
          toggleModal={setShowSettingsModal}
          modalTitle="Configure claims settings"
          highlightColor={page.highlight}
          closeLabel="Cancel"
        >
          <Box gap="1.25rem" pad={{ vertical: '0.5rem' }}>
            <Box direction="column" gap="0.75rem">
              <ProductFormCheckBoxInput
                borderColor={page.inputBorder}
                focusHighlight={page.focusHighlight}
                checkmarkColor={page.inputIcon}
                label="Enable automatic claim processing"
                value={settings.enableAutoProcessing}
                controlled
                handleFormValues={(val) => handleSettingChange('enableAutoProcessing', val)}
              />
              <Box direction="column" gap="0.75rem" pad={{ left: '2.5rem' }}>
                <Text color={page.modalText} size="0.85rem">
                  If you choose to enable automatic claim processing, you can
                  set up rules for how different types of videos are handled
                  when a claim is detected. This allows you to automatically
                  monetize or take down videos based on their characteristics.
                </Text>
                <Text color={page.modalText} size="0.85rem">
                  If left disabled, you will need to manually review
                  and process claims on this page, allowing for more
                  control over how each claim is handled.
                </Text>
              </Box>
            </Box>
            <Box
              direction="column"
              gap="1.25rem"
              round="var(--app-radius)"
              border={{ color: page.highlight, size: '1px', side: 'all' }}
              pad="0.75rem"
            >
              <Box direction="row" gap="0.75rem" align="center">
                <Text color={page.modalText} size="1rem" weight={500} style={{ whiteSpace: 'nowrap' }}>
                  Short videos (less than 3 minutes):
                </Text>
                <FlexibleSelectDropdown
                  textColor={page.modalText}
                  flexibleOptions={selectOptions}
                  displayKey="label"
                  valueKey="value"
                  selectedOption={settings.shortVideos}
                  handleItemSelect={(val) => handleSettingChange('shortVideos', val)}
                  noSelectionLabel="Enable to select an action"
                  buttonBg={page.inputBg}
                  borderColor={page.inputBorder}
                  disabled={!settings.enableAutoProcessing}
                />
              </Box>
              <Box direction="row" gap="0.75rem" align="center">
                <Text color={page.modalText} size="1rem" weight={500} style={{ whiteSpace: 'nowrap' }}>
                  Long-form videos (3 minutes or longer):
                </Text>
                <FlexibleSelectDropdown
                  textColor={page.modalText}
                  flexibleOptions={selectOptions}
                  displayKey="label"
                  valueKey="value"
                  selectedOption={settings.longFormVideos}
                  handleItemSelect={(val) => handleSettingChange('longFormVideos', val)}
                  noSelectionLabel="Enable to select an action"
                  buttonBg={page.inputBg}
                  borderColor={page.inputBorder}
                  disabled={!settings.enableAutoProcessing}
                />
              </Box>
              <Box direction="row" gap="0.75rem" align="center">
                <Text color={page.modalText} size="1rem" weight={500} style={{ whiteSpace: 'nowrap' }}>
                  Private videos (invitation only):
                </Text>
                <FlexibleSelectDropdown
                  textColor={page.modalText}
                  flexibleOptions={selectOptions}
                  displayKey="label"
                  valueKey="value"
                  selectedOption={settings.privateVideos}
                  handleItemSelect={(val) => handleSettingChange('privateVideos', val)}
                  noSelectionLabel="Enable to select an action"
                  buttonBg={page.inputBg}
                  borderColor={page.inputBorder}
                  disabled={!settings.enableAutoProcessing}
                />
              </Box>
            </Box>
            <Box width="100%" align="center" justify="center" pad={{ top: '1rem' }}>
              <AppButton
                overrideHover
                level="dynamicLarge"
                color={button.primaryText}
                bgColor={button.primaryBg}
                fontWeight={600}
                label={submitting ? 'Saving...' : 'Save settings'}
                onClick={handleSettingsSubmit}
                disabled={!canSubmit}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1rem' }}
        height="100%"
        gap="1rem"
      >
        {claimsView === 'automated' && (
          <InfoNotice header="Claims data update schedule">
            <Box direction="column" gap="0.4rem">
              <Text color="var(--page-primary-text)" size="0.9rem">
                This page displays claims data from a daily refresh cycle.
                Updates may not be visible until the next sync.
              </Text>
              <Box direction="row" wrap gap="0.35rem" align="center">
                <Text color="var(--page-primary-text)" size="0.85rem" weight={500}>
                  Search bar in filter supports:
                </Text>
                {[
                  'Video title',
                  'Channel name',
                  'Video ID',
                  'Asset ID',
                  'Asset title',
                  'Channel ID (UC...)',
                ].map((tag) => (
                  <Box
                    key={tag}
                    background="var(--page-input-bg)"
                    border={{ color: 'var(--page-input-border)', size: '1px', side: 'all' }}
                    round="4px"
                    pad={{ vertical: '0.1rem', horizontal: '0.4rem' }}
                  >
                    <Text size="0.8rem" color="var(--page-input-text)" weight={500}>
                      {tag}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </InfoNotice>
        )}
        {claimsView === 'manual' && (
          <InfoNotice
            header="Manual claim requests"
            text="When a claim is created on YouTube, it will appear under Automated Claims after the next data sync."
          />
        )}
        {claimsView === 'automated' && (
          <ClaimsTablePartial
            small={small}
            claimsData={claimsData}
            companyAssetConfig={assetConfig}
            showAssetLabelFilter={!assetId}
            autoClaimProcessing={Boolean(autoClaimProcessing)}
            submitMonetizationRequest={submitMonetizationRequest}
            submitClaimReleaseRequest={handleSubmitClaimRelease}
            submitRevertReleaseRequest={handleSubmitReleaseRevert}
            claimDetailsPath={claimDetailsPath}
            onQueryChange={handleQueryChange}
            onClickExport={handleCsvExport}
            refreshData={refreshData}
            handlePage={handlePage}
            currentPage={currentPage}
            query={query}
            loading={loading}
            paymentPending={paymentPending}
          />
        )}
        {claimsView === 'manual' && (
          <ManualClaimsRequestsTablePartial
            small={small}
            loading={loading}
            data={manualClaimRequestsData}
            handlePage={handleManualPage}
            currentPage={manualClaimPage}
            query={manualQuery}
            onQueryChange={handleManualQueryChange}
            paymentPending={paymentPending}
            onOpenDetail={(manualClaimId) => navigate(
              paths.productManualClaimRequest.replace(':id', manualClaimId),
            )}
          />
        )}

      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    claimsData: state.contentIdClaims.claims,
    manualClaimRequestsData: state.contentIdClaims.manualClaimRequests,

    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchClaimsData: initiateFetchClaimsRequest,
      fetchAllClaimData: initiateFetchAllClaimsRequest,
      clearClaimsData: clearContentIdClaimsRequest,
      fetchManualClaimRequests: initiateFetchManualClaimRequests,
      clearManualClaimsData: clearManualClaimRequests,
      submitClaimReleaseRequest: initiateCreateClaimReleaseRequest,
      submitRevertReleaseRequest: initiateRevertClaimReleaseRequest,
      updateClaimsSettings: initiateUpdateClaimsSettingsRequest,
      submitMonetizationRequest: initiateMonetizationRequest,
    },
    dispatch,
  );
}

ClaimsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchClaimsData: PropTypes.func.isRequired,
  fetchAllClaimData: PropTypes.func.isRequired,
  clearClaimsData: PropTypes.func.isRequired,
  fetchManualClaimRequests: PropTypes.func.isRequired,
  clearManualClaimsData: PropTypes.func.isRequired,
  submitClaimReleaseRequest: PropTypes.func.isRequired,
  submitRevertReleaseRequest: PropTypes.func.isRequired,
  manualClaimRequestsData: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.object),
    pagination: PropTypes.object,
  }),
  claimsData: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        assetId: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        contentType: PropTypes.string,
        engagedViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isPartnerUploaded: PropTypes.bool,
        thirdPartyClaim: PropTypes.bool,
        kind: PropTypes.string,
        origin: PropTypes.shape({
          source: PropTypes.string,
        }),
        status: PropTypes.string.isRequired,
        studioInfo: PropTypes.shape({
          claimUrl: PropTypes.string,
          videoUrl: PropTypes.string,
        }),
        timeCreated: PropTypes.string.isRequired,
        timeStatusLastModified: PropTypes.string,
        videoId: PropTypes.string,
        videoTitle: PropTypes.string,
        videoViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ).isRequired,
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }).isRequired,
  }),
  updateClaimsSettings: PropTypes.func.isRequired,
  submitMonetizationRequest: PropTypes.func.isRequired,
  featureConfig: PropTypes.shape({
    assetConfig: PropTypes.arrayOf(PropTypes.any),
    autoClaimProcessing: PropTypes.bool,
    autoProcessingConfig: PropTypes.shape({
      shortVideos: PropTypes.string,
      longFormVideos: PropTypes.string,
      privateVideos: PropTypes.string,
    }),
  }),
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withProductAuth(ClaimsListPage));
